'use client';

import { TempcoverLogo } from '@/components/icons/TempcoverLogo/TempcoverLogo';
import TrustPilotWidget from '@/components/ui/TrustPilot/TrustPilot';
import { NavigationItem } from '@/types/navigation';
import { useEffect, useRef, useState } from 'react';
import { Hamburger } from './Hamburger';
import { Navigation } from './Navigation';
import { Breakpoint, useIsBreakpoint } from '@/hooks/useIsBreakpoint';
import { TopLinks } from './TopLinks';

const host = process.env.NEXT_PUBLIC_HOST_NAME;

const Header = ({ data }: { data?: NavigationItem[] }) => {
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const isMobile = useIsBreakpoint(Breakpoint.LG);
  const headerRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    if (!open && headerRef.current) {
      headerRef.current.scrollIntoView();
    }
    setOpen((prev) => !prev);
  };

  const closeMenu = () => {
    if (isMobile) return;

    setOpen(false);
    setActiveSection('');
  };

  const handleMouseEnter = (section: string) => {
    if (isMobile) return;

    setActiveSection(section);
    setOpen(true);
  };

  const handleClick = (section: string) => {
    if (isMobile) {
      setActiveSection(section);
      setOpen(true);
    }
  };

  /*
   * Used on mobile to go back to the main menu
   */
  const handleBack = () => {
    if (isMobile) {
      setActiveSection('');
      setOpen(true);
    }
  };

  useEffect(() => {
    if (isMobile) {
      document.body.classList.toggle('overflow-hidden', open);
    }
  }, [open, isMobile]);

  return (
    <>
      <TopLinks className="hidden lg:block" />

      <div
        ref={headerRef}
        onMouseLeave={closeMenu}
        className="bg-background-200 border-b-dark-100 sticky top-0 z-3 flex w-full items-center gap-4 border-b py-4 align-middle lg:gap-0 lg:pb-4.5"
      >
        <div className="tc-container gap-regular flex">
          <Logo />

          {data && (
            <Navigation
              className="order-0 lg:order-1 lg:grow"
              data={data}
              open={open}
              activeSection={activeSection}
              onClick={handleClick}
              onEnter={handleMouseEnter}
              onBack={handleBack}
            />
          )}

          <div className="order-2 block lg:hidden">
            <Hamburger isOpen={open} onClick={toggleMenu} />
          </div>
        </div>
      </div>

      <div className="relative">
        <TrustPilotWidget variant="dark" />
      </div>

      {open && <div className="bg-dark-400/50 pointer-none fixed inset-0 z-1" />}
    </>
  );
};

const Logo = () => (
  <div className="logo align-center order-1 flex grow justify-center lg:order-0 lg:justify-start">
    <a
      href={host}
      className="flex justify-center lg:justify-start"
      title="Tempcover - Affordable cover, unbelievably fast"
    >
      <TempcoverLogo />
    </a>
  </div>
);

export default Header;
